import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {z} from 'zod';
import {getDepartmentMaster} from '../../../core/repositories/reportsRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {ReportByDepartmentPage} from '../../../features/reports/components/ReportByDepartmentPage';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  departmentName?: string;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};

const QueryParameter = z.object({
  departmentId: z.string(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const props: Props = {};

    const {departmentId} = context.validatedQuery;
    const {cookies} = context.req;

    const departmentMaster = await getDepartmentMaster({
      queryParams: {isPopular: 'true'},
    });
    const departmentName = departmentMaster?.ok
      ? departmentMaster.value.find((item) => item.id === Number(departmentId))
          ?.name
      : '';

    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          ...props,
          departmentName,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          ...props,
          departmentName,
        },
      };
    }

    return {
      props: {
        ...props,
        departmentName,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  departmentName,
  userDataForAnalytics,
}: Props) => {
  return (
    <>
      <ReportByDepartmentPage departmentName={departmentName} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;

export default Page;
